import React from 'react';
import 'moment/locale/pt-br';
import moment from 'moment';
import Timeline from '@mui/lab/Timeline';
import TimelineItem from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineOppositeContent from '@mui/lab/TimelineOppositeContent';
import { Container, ContainerArea } from './styles';
import { formatHistoricStatus } from 'utils/policyStatus';
import { formatCurrency } from 'react-data-formatter';
import { maskZipCode } from 'utils/masks';

const cancellationReasons = {
  total_loss: 'Veículo indenizado',
  customer_request: 'Desistiu do seguro',
  commercial_dissatisfaction: 'Insatisfação comercial',
  financial_difficulty: 'Sem condições financeiras',
  vehicle_sold: 'Veículo vendido',
  vehicle_replacement: 'Substituição de veículo',
  payment_default: 'Inadimplência',
};

function HistoryItem({ data, setAttendanceAction, setAttendanceData }) {
  const openLink = (link) => {
    window.open(link, '_blank');
  };

  const openAttendance = (attendance) => {
    setAttendanceAction('view');
    setAttendanceData(attendance);
  };

  const formatCoverageChanges = (updateData) => {
    try {
      const data = JSON.parse(updateData || '{}');

      const oldCov = data.old_data?.coverages || {};
      const newCov = data.new_data?.coverages || {};

      const blocks = [];

      const getOldValue = (value) => (value ? value : 'Sem cobertura');

      if (oldCov.basic !== newCov.basic) {
        blocks.push(`Coberturas basicas :
De: ${getOldValue(oldCov.basic)}
Para: ${newCov.basic || 'Sem cobertura'}`);
      }

      if (newCov.support && oldCov.support !== newCov.support) {
        blocks.push(`Coberturas assistencia :
De: ${getOldValue(oldCov.support)}
Para: ${newCov.support}`);
      }

      const oldOptionals = oldCov.optionals || [];
      const newOptionals = newCov.optionals || [];

      const addedOptionals = newOptionals.filter((opt) => !oldOptionals.includes(opt));
      const removedOptionals = oldOptionals.filter((opt) => !newOptionals.includes(opt));

      if (addedOptionals.length) {
        let optionalsText = 'Coberturas opcionais :\n';
        optionalsText += addedOptionals.map((opt) => `De: Sem cobertura\nPara: ${opt}`).join('\n');
        blocks.push(optionalsText);
      }

      if (removedOptionals.length) {
        let removedText = 'Coberturas opcionais removidas :\n';
        removedText += removedOptionals.map((opt) => `De: ${opt}\nPara: Sem cobertura`).join('\n');
        blocks.push(removedText);
      }

      blocks.push(`Valor :
De: ${formatCurrency(data.old_data?.value || 0)}
Para: ${formatCurrency(data.new_data?.value || 0)}`);

      return blocks.join('\n\n');
    } catch (e) {
      return 'Coberturas atualizadas';
    }
  };

  return (
    <>
      <Container>
        <ContainerArea>
          <Timeline style={{ width: '100%', marginLeft: -150 }}>
            {data?.map((item, index) => (
              <TimelineItem key={index}>
                <TimelineOppositeContent style={{ fontSize: 12 }}>
                  {moment(item?.created_at)?.format('DD/MM/YYYY')} <br />
                  <p style={{ margin: 0, fontSize: 12, color: '#868686' }}>{moment(item?.created_at)?.format('HH:mm')}</p>
                </TimelineOppositeContent>

                <TimelineSeparator>
                  <TimelineDot
                    style={{ backgroundColor: formatHistoricStatus(item?.action)?.color, cursor: 'pointer' }}
                    onClick={() => {
                      item.action === 'attendance' && openAttendance(item);
                      item.action === 'new_proposal' && openLink(`/backoffice/detalhes-da-proposta/${item?.proposal?.id}`);
                      // item.action === 'inspection' && openLink(`${item?.inspection?.inspection_code}`);
                      item.action === 'new_policy' && openLink(`/backoffice/detalhes-do-contrato/${item?.policy?.id}`);
                      item.action === 'new_charge' && openLink(`/backoffice/faturas/${item?.id_policy}`);
                      item.action === 'new_claim' && openLink(`/backoffice/detalhes-do-sinistro/${item?.metadata}`);
                      item.action === 'charge_paid' && openLink(`/backoffice/faturas/${item?.id_policy}`);
                      item.action === 'charge_failed' && openLink(`/backoffice/faturas/${item?.id_policy}`);
                      item.action === 'new_cancelation' && openLink(`/backoffice/cancelamentos`);
                      item.action === 'cancelation_updated' && openLink(`/backoffice/cancelamentos`);
                      item.action === 'new_complaint' &&
                        openLink(`//backoffice/detalhes-reclamacao/${item?.complaint?.id_complaint}`);
                      item.action === 'complaint_updated' &&
                        openLink(`//backoffice/detalhes-reclamacao/${item?.complaint?.id_complaint}`);
                      item.action === 'complaint_finished' &&
                        openLink(`//backoffice/detalhes-reclamacao/${item?.complaint?.id_complaint}`);
                    }}
                  >
                    {formatHistoricStatus(item?.action)?.icon}
                  </TimelineDot>
                  {index !== data.length - 1 && <TimelineConnector />}
                </TimelineSeparator>

                <TimelineContent style={{ fontSize: 14 }}>
                  {item.action === 'attendance' ? 'Atendimento registrado' : item?.description} <br />
                  <p
                    style={{
                      margin: 0,
                      fontSize: 12,
                      color: '#868686',
                      maxWidth: '60%',
                      textAlign: 'justify',
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {item.action === 'new_proposal' && `Protocolo: ${item?.proposal?.protocol}`}
                    {item.action === 'inspection' && `Vistoria: ${item?.inspection?.inspection_code}`}
                    {item.action === 'new_policy' && `Contrato: ${item?.policy?.policy_number}`}
                    {item.action === 'attendance' && `${item?.description}`}
                    {item.action === 'new_charge' && formatCurrency(item?.charge?.value || 0)}
                    {item.action === 'charge_paid' && formatCurrency(item?.charge?.value || 0)}
                    {item.action === 'charge_failed' &&
                      `${item?.transaction?.TB_CONSTANT_PAY_METHODS?.name} - ${formatCurrency(item?.transaction?.value) || 0}`}
                    {item.action === 'new_cancelation' && `Motivo: ${item?.cancelation?.TB_CONSTANT_CANCELLATION_REASON?.name}`}

                    {item.action === 'not_renew' && (
                      <>
                        Cancelado por: {item?.cancelation?.TB_USERS?.name || 'Sistema'}
                        <br />
                        Motivo: {cancellationReasons[item?.cancelation?.substatus] || 'n/a'}
                      </>
                    )}

                    {item.action === 'cancelation_updated' && `Observação: ${item?.cancelation?.description}`}

                    {item.action === 'new_complaint' && `Motivo: ${item?.complaint?.TB_COMPLAINT?.TB_SUSEP_TYPE_COMPLAINT?.name}`}

                    {item.action === 'complaint_updated' &&
                      `Motivo: ${item?.complaint?.TB_COMPLAINT?.TB_SUSEP_TYPE_COMPLAINT?.name}`}

                    {item.action === 'complaint_finished' &&
                      `Status: ${item?.complaint?.TB_COMPLAINT?.TB_SUSEP_STATUS_COMPLAINT?.name}`}

                    {item.action === 'plate_updated' &&
                      (() => {
                        try {
                          const data = JSON.parse(item.update_data || '{}');
                          return `Placa: ${data.old_plate || 'N/A'} ➔ ${data.new_plate || 'N/A'}`;
                        } catch (e) {
                          return 'Placa atualizada';
                        }
                      })()}

                    {item.action === 'registration_updated' &&
                      (() => {
                        try {
                          const data = JSON.parse(item.update_data || '{}');

                          const oldZip = maskZipCode(data.old_address?.zip_code || 'N/A');
                          const newZip = maskZipCode(data.new_address?.zip_code || 'N/A');
                          const newCity = data.new_address?.city || '';
                          const newState = data.new_address?.state || '';

                          const oldValue = data.old_value;
                          const newValue = data.new_value;

                          const hasValueChange = oldValue !== undefined && newValue !== undefined && oldValue !== newValue;

                          return (
                            <>
                              {`CEP: ${oldZip} ➔ ${newZip} (${newCity}/${newState})`}

                              {hasValueChange && (
                                <>
                                  <br />
                                  <strong>Valor:</strong>
                                  <br />
                                  {`De: ${formatCurrency(oldValue)}`}
                                  <br />
                                  {`Para: ${formatCurrency(newValue)}`}
                                </>
                              )}
                            </>
                          );
                        } catch (e) {
                          return 'Endereço atualizado';
                        }
                      })()}

                    {item.action === 'coverage_updated' && formatCoverageChanges(item.update_data)}

                    {(item.action === 'plate_updated' ||
                      item.action === 'registration_updated' ||
                      item.action === 'coverage_updated') && (
                      <div style={{ marginTop: 4, fontWeight: 600 }}>Solicitado por: {item?.author_name || '--'}</div>
                    )}
                  </p>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        </ContainerArea>
      </Container>
    </>
  );
}

export default HistoryItem;
